const OPEN_TAG = '<RESPONSE>'
const CLOSE_TAG = '</RESPONSE>'

function isWhitespace(char) {
  return char === ' ' || char === '\n' || char === '\r' || char === '\t'
}

function isHex(char) {
  return /^[0-9a-f]$/i.test(char)
}

/**
 * 针对流式预览设计的、允许输入在任意位置结束的 JSON 解析器。
 *
 * 把当前 EOF 当作临时结束位置，产出已经读到的对象、数组和字符串内容。
 * 这就是“影子补全”层：只生成可渲染快照，绝不修改服务端真实流。
 *
 * 设计来源于 vue2-streaming-response-demo 的 ResponseStreamParser，
 * 适配本项目数据形状（type / message / options / doctors）。
 */
class PartialJsonParser {
  constructor(source) {
    this.source = source
    this.index = 0
  }

  parse() {
    this.skipWhitespace()
    const result = this.parseValue()
    return result.started ? result.value : undefined
  }

  skipWhitespace() {
    while (this.index < this.source.length && isWhitespace(this.source[this.index])) {
      this.index += 1
    }
  }

  parseValue() {
    this.skipWhitespace()

    if (this.index >= this.source.length) {
      return { started: false, complete: false, value: undefined }
    }

    const char = this.source[this.index]
    if (char === '{') return this.parseObject()
    if (char === '[') return this.parseArray()
    if (char === '"') return this.parseString()
    if (char === 't') return this.parseLiteral('true', true)
    if (char === 'f') return this.parseLiteral('false', false)
    if (char === 'n') return this.parseLiteral('null', null)
    if (char === '-' || /[0-9]/.test(char)) return this.parseNumber()

    return { started: false, complete: false, value: undefined }
  }

  parseObject() {
    const value = {}
    this.index += 1

    // eslint-disable-next-line no-constant-condition
    while (true) {
      this.skipWhitespace()

      if (this.index >= this.source.length) {
        return { started: true, complete: false, value }
      }

      if (this.source[this.index] === '}') {
        this.index += 1
        return { started: true, complete: true, value }
      }

      if (this.source[this.index] === ',') {
        this.index += 1
        continue
      }

      // 尚未完整生成的字段名无法映射到 UI，当前快照先忽略，下一 chunk 会重新解析。
      if (this.source[this.index] !== '"') {
        return { started: true, complete: false, value }
      }

      const key = this.parseString()
      if (!key.complete) {
        return { started: true, complete: false, value }
      }

      this.skipWhitespace()
      if (this.index >= this.source.length || this.source[this.index] !== ':') {
        return { started: true, complete: false, value }
      }

      this.index += 1
      this.skipWhitespace()
      const child = this.parseValue()
      if (!child.started) {
        return { started: true, complete: false, value }
      }

      value[key.value] = child.value
      this.skipWhitespace()

      if (this.index >= this.source.length) {
        return { started: true, complete: false, value }
      }

      if (this.source[this.index] === '}') {
        this.index += 1
        return { started: true, complete: true, value }
      }

      if (this.source[this.index] === ',') {
        this.index += 1
        continue
      }

      return { started: true, complete: false, value }
    }
  }

  parseArray() {
    const value = []
    this.index += 1

    // eslint-disable-next-line no-constant-condition
    while (true) {
      this.skipWhitespace()

      if (this.index >= this.source.length) {
        return { started: true, complete: false, value }
      }

      if (this.source[this.index] === ']') {
        this.index += 1
        return { started: true, complete: true, value }
      }

      if (this.source[this.index] === ',') {
        this.index += 1
        continue
      }

      const child = this.parseValue()
      if (!child.started) {
        return { started: true, complete: false, value }
      }

      // 主动保留未完成的对象或字符串，让当前卡片也能逐字段出现。
      value.push(child.value)
      this.skipWhitespace()

      if (this.index >= this.source.length) {
        return { started: true, complete: false, value }
      }

      if (this.source[this.index] === ']') {
        this.index += 1
        return { started: true, complete: true, value }
      }

      if (this.source[this.index] === ',') {
        this.index += 1
        continue
      }

      return { started: true, complete: false, value }
    }
  }

  parseString() {
    let value = ''
    this.index += 1

    while (this.index < this.source.length) {
      const char = this.source[this.index]
      this.index += 1

      if (char === '"') {
        return { started: true, complete: true, value }
      }

      if (char !== '\\') {
        value += char
        continue
      }

      if (this.index >= this.source.length) {
        return { started: true, complete: false, value }
      }

      const escaped = this.source[this.index]
      this.index += 1
      const escapes = {
        '"': '"',
        '\\': '\\',
        '/': '/',
        b: '\b',
        f: '\f',
        n: '\n',
        r: '\r',
        t: '\t',
      }

      if (Object.prototype.hasOwnProperty.call(escapes, escaped)) {
        value += escapes[escaped]
        continue
      }

      if (escaped !== 'u') {
        return { started: true, complete: false, value }
      }

      let hex = ''
      while (hex.length < 4 && this.index < this.source.length && isHex(this.source[this.index])) {
        hex += this.source[this.index]
        this.index += 1
      }

      if (hex.length < 4) {
        return { started: true, complete: false, value }
      }

      value += String.fromCharCode(Number.parseInt(hex, 16))
    }

    return { started: true, complete: false, value }
  }

  parseLiteral(token, value) {
    const rest = this.source.slice(this.index)

    if (rest.startsWith(token)) {
      this.index += token.length
      return { started: true, complete: true, value }
    }

    if (token.startsWith(rest)) {
      this.index = this.source.length
      return { started: true, complete: false, value }
    }

    return { started: false, complete: false, value: undefined }
  }

  parseNumber() {
    const start = this.index
    while (this.index < this.source.length && /[0-9eE+.-]/.test(this.source[this.index])) {
      this.index += 1
    }

    let token = this.source.slice(start, this.index)
    while (token && !Number.isFinite(Number(token))) {
      token = token.slice(0, -1)
    }

    if (!token) {
      return { started: false, complete: false, value: undefined }
    }

    return {
      started: true,
      complete: this.index < this.source.length,
      value: Number(token),
    }
  }
}

function parsePartialJson(source) {
  try {
    return new PartialJsonParser(source).parse()
  } catch (_error) {
    return undefined
  }
}

function withoutPartialTagSuffix(text, tag) {
  const maxLength = Math.min(text.length, tag.length - 1)
  for (let length = maxLength; length > 0; length -= 1) {
    if (text.endsWith(tag.slice(0, length))) {
      return text.slice(0, -length)
    }
  }
  return text
}

function normalizeResponse(value) {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return { type: '', message: '', options: [], doctors: [], packages: [], next_actions: [] }
  }

  const options = Array.isArray(value.options)
    ? value.options.filter((item) => typeof item === 'string' && item.trim())
    : []

  const doctors = Array.isArray(value.doctors)
    ? value.doctors.filter((item) => item && typeof item === 'object' && !Array.isArray(item))
    : []

  const packages = Array.isArray(value.packages)
    ? value.packages.filter((item) => item && typeof item === 'object' && !Array.isArray(item))
    : []

  const nextActions = Array.isArray(value.next_actions)
    ? value.next_actions.filter((item) => typeof item === 'string' && item.trim())
    : []

  return {
    type: typeof value.type === 'string' ? value.type : '',
    message: typeof value.message === 'string' ? value.message : '',
    options,
    doctors,
    packages,
    next_actions: nextActions,
  }
}

export function createEmptySnapshot() {
  return {
    phase: 'idle',
    preamble: '',
    response: { type: '', message: '', options: [], doctors: [], packages: [], next_actions: [] },
    rawText: '',
    syntheticDocument: '',
    strict: false,
    done: false,
    error: null,
  }
}

function buildBareJsonSnapshot(rawText, streamEnded) {
  const source = rawText.trim()
  // 只处理以 { 开头的输出，视为未包裹 <RESPONSE> 标签的裸 JSON 结构化响应
  if (!source.startsWith('{')) return null

  const partial = normalizeResponse(parsePartialJson(source))

  // 裸 JSON 必须带非空 type 才认定为结构化响应（与 parseResponseWrapper 的裸 JSON 路径保持一致）。
  // type 字段尚未出现时先按"待定"处理：preamble 置空、不输出文本，避免 JSON 原文闪现；
  // 若流结束仍无 type，则返回 null 走老版本行为 —— 整段文本作为普通正文展示，不会出现空白消息。
  if (!partial.type) {
    if (streamEnded) return null
    return {
      phase: 'preamble',
      preamble: '',
      response: partial,
      rawText,
      syntheticDocument: '',
      strict: false,
      done: false,
      error: null,
    }
  }

  if (streamEnded) {
    try {
      const response = normalizeResponse(JSON.parse(source))
      return {
        phase: 'done',
        preamble: '',
        response,
        rawText,
        syntheticDocument: JSON.stringify(response),
        strict: true,
        done: true,
        error: null,
      }
    } catch (error) {
      const response = normalizeResponse(parsePartialJson(source))
      return {
        phase: 'error',
        preamble: '',
        response,
        rawText,
        syntheticDocument: JSON.stringify(response),
        strict: false,
        done: false,
        error: `最终 JSON 严格校验失败：${error.message}`,
      }
    }
  }

  return {
    phase: 'streaming',
    preamble: '',
    response: partial,
    rawText,
    syntheticDocument: JSON.stringify(partial),
    strict: false,
    done: false,
    error: null,
  }
}

export class ResponseStreamParser {
  constructor() {
    this.rawText = ''
    this.streamEnded = false
  }

  push(delta) {
    if (this.streamEnded) {
      throw new Error('Cannot push after finish()')
    }

    if (typeof delta !== 'string') {
      throw new TypeError('ResponseStreamParser.push(delta) expects a string')
    }

    // rawText 永远只追加真实 delta，补齐内容只存在于 getSnapshot 的返回值中。
    this.rawText += delta
    return this.getSnapshot(false)
  }

  finish(finalDelta = '') {
    if (finalDelta) {
      this.push(finalDelta)
    }
    this.streamEnded = true
    return this.getSnapshot(true)
  }

  getSnapshot(streamEnded) {
    const openIndex = this.rawText.indexOf(OPEN_TAG)

    if (openIndex === -1) {
      // 兼容未包裹 <RESPONSE> 标签的裸 JSON 流式输出：以 { 开头即进入结构化解析，
      // 不再当作 preamble 文本逐字输出。
      const bareSnapshot = buildBareJsonSnapshot(this.rawText, streamEnded)
      if (bareSnapshot) return bareSnapshot

      return {
        ...createEmptySnapshot(),
        phase: streamEnded ? 'error' : 'preamble',
        preamble: withoutPartialTagSuffix(this.rawText, OPEN_TAG),
        rawText: this.rawText,
        syntheticDocument: withoutPartialTagSuffix(this.rawText, OPEN_TAG),
        error: streamEnded ? `流已结束，但没有收到 ${OPEN_TAG}` : null,
      }
    }

    const contentStart = openIndex + OPEN_TAG.length
    const closeIndex = this.rawText.indexOf(CLOSE_TAG, contentStart)
    const jsonSource = this.rawText.slice(
      contentStart,
      closeIndex === -1 ? this.rawText.length : closeIndex,
    )
    const preamble = this.rawText.slice(0, openIndex)

    if (closeIndex !== -1) {
      try {
        // 真正的结束标签到达后，必须回到原始 JSON 做严格校验。
        const response = JSON.parse(jsonSource)
        return {
          phase: 'done',
          preamble,
          response: normalizeResponse(response),
          rawText: this.rawText,
          syntheticDocument: `${preamble}${OPEN_TAG}${JSON.stringify(response)}${CLOSE_TAG}`,
          strict: true,
          done: true,
          error: null,
        }
      } catch (error) {
        const response = normalizeResponse(parsePartialJson(jsonSource))
        return {
          phase: 'error',
          preamble,
          response,
          rawText: this.rawText,
          syntheticDocument: `${preamble}${OPEN_TAG}${JSON.stringify(response)}${CLOSE_TAG}`,
          strict: false,
          done: false,
          error: `最终 JSON 严格校验失败：${error.message}`,
        }
      }
    }

    const response = normalizeResponse(parsePartialJson(jsonSource))
    return {
      phase: streamEnded ? 'error' : 'streaming',
      preamble,
      response,
      rawText: this.rawText,
      // 每个 chunk 都构造闭合的影子文档，仅供当前帧预览。
      syntheticDocument: `${preamble}${OPEN_TAG}${JSON.stringify(response)}${CLOSE_TAG}`,
      strict: false,
      done: false,
      error: streamEnded ? `流已结束，但没有收到 ${CLOSE_TAG}` : null,
    }
  }
}

export const responseTags = Object.freeze({ open: OPEN_TAG, close: CLOSE_TAG })
